import React, { useEffect, useState } from "react";
import BookReviewManager from "../../../services/home/BookReviewManager";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import config from "../../../config";
import { Link } from "react-router-dom";

const BookReviews = () => {
  const { id } = useParams();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Calculate star ratings percentage
  const getRatingSummary = () => {
    const summary = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((review) => {
      summary[review.rating] = (summary[review.rating] || 0) + 1;
    });
    return summary;
  };

  const averageRating = () => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, r) => sum + r.rating, 0);
    return (total / reviews.length).toFixed(1);
  };

  useEffect(() => {
    async function fetchReviews() {
      try {
        const data = await BookReviewManager.getReviewsByBook(id);
        setReviews(data || []);
      } catch (error) {
        console.error("Failed to fetch reviews:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchReviews();
  }, [id]);

  const ratingSummary = getRatingSummary();
  const avgRating = averageRating();

  if (loading) {
    return <div className="text-primary">Loading reviews...</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="bg-primary-shade1 rounded-3xl p-8 md:p-12 shadow-2xl max-w-7xl mx-auto mt-12 font-nunito"
    >
      <div className="flex flex-col md:flex-row md:items-start gap-8 md:gap-12">
        {/* Left side: Ratings Summary */}
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-6 text-text-muted">
            Customer Review
          </h2>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex text-yellow-400">
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  size={20}
                  fill={i < Math.round(avgRating) ? "#FACC15" : "none"}
                  strokeWidth={1}
                />
              ))}
            </div>
            <span className="text-lg font-semibold">{avgRating} out of 5</span>
          </div>

          {/* Progress Bars */}
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center gap-2 text-sm">
                <div className="w-12">{star} star</div>
                <div className="flex-1 bg-gray-300 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-primary h-3"
                    style={{
                      width: `${
                        (ratingSummary[star] / reviews.length) * 100 || 0
                      }%`,
                    }}
                  />
                </div>
                <div className="w-8 text-right">
                  {Math.round((ratingSummary[star] / reviews.length) * 100) ||
                    0}
                  %
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right side: Reviews */}
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-6 text-text-muted">
            Top Reviews
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(showAll ? reviews : reviews.slice(0, 2)).map((review) => (
              <div
                key={review.reviewId}
                className="bg-white rounded-xl p-4 shadow hover:shadow-lg transition space-y-2 text-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                    {review.user?.profilePhotoUrl ? (
                      <img
                        src={`${config.url}/userImages/${review.user.profilePhotoUrl}`}
                        alt="User Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-base">
                        {review.user?.firstName?.[0]}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      {review.user?.firstName} {review.user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(
                        review.creationDate[0],
                        review.creationDate[1] - 1,
                        review.creationDate[2]
                      ).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex text-yellow-400 mt-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star
                      key={i}
                      size={14}
                      fill={i < review.rating ? "#FACC15" : "none"}
                      strokeWidth={1}
                    />
                  ))}
                </div>

                <p className="text-[11px] text-primary font-semibold">
                  Verified Purchase
                </p>

                <p className="text-gray-700 text-sm leading-snug line-clamp-4">
                  {review.review}
                </p>

                <div className="flex gap-4 text-xs text-primary mt-2">
                  <button className="hover:underline">Helpful</button>
                  <button className="hover:underline">Report</button>
                </div>
              </div>
            ))}
          </div>
          {reviews.length > 2 && (
            <div className="text-right mt-6">
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-sm text-primary hover:underline font-semibold"
              >
                {showAll ? "Show Less" : "See All Reviews"}
              </button>
            </div>
          )}

          {/* See more */}
          <div className="text-right mt-6"></div>
        </div>
      </div>
    </motion.div>
  );
};

export default BookReviews;
